/**
 * Chapter 6 Optimized Layout Component
 * 
 * NEW UNIFIED LAYOUT (matching Chapter 4):
 * - 70% viewport height: Video/image section (top)
 * - 30% viewport height: Text section (bottom)
 * - No scroll, everything fits in viewport
 * - Clean separation between visual and text
 * 
 * VIDEO PLACEHOLDERS READY: Once videos are available, simply add videoId to spread data
 */

import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { Chapter6CueChoice } from "@/components/chapter-6-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { saveBookmark } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { ArrowLeft, ArrowRight, Home, BookOpen, Coffee } from "lucide-react";

// TypeScript interface for spread data (makes videoId optional for video placeholder system)
interface SpreadData {
  spread: number;
  section: string;
  isMapPage?: boolean;
  videoId?: string; // 🎬 Optional video ID (ready for when videos arrive)
  imageUrl?: string;
  text?: string;
  cue?: string;
  cueText?: string;
}// Chapter 6 spreads data (VIDEO IDs READY TO BE ADDED)
const chapter6Data: {
  title: string;
  subtitle: string;
  cue: string;
  cueIcon: any;
  cueColor: string;
  bgColor: string;
  spreads: SpreadData[];
} = {
  title: "Chapter 6: The Sharing Tree",
  subtitle: "Sharing & Connection",
  cue: "Dew Cup",
  cueIcon: Coffee,
  cueColor: "text-cyan-600",
  bgColor: "bg-cyan-50",
  spreads: [
    {
      spread: 0,
      section: "map",
      isMapPage: true, // Forest Journey Map showing path to Chapter 6
    },
    {
      spread: 1,
      section: "story",
      videoId: "949eefb635e29307d477756e097fc03d", // ✅ Page 1 - Cloudflare Stream video
      text: "Leaving the glowing Gratitude Garden, Mila spotted tiny, silver squirrel footprints on the map. \"They lead to the Sharing Tree!\" Bella exclaimed. \"I bet we'll learn our next lesson there.\""
    },
    {
      spread: 2,
      section: "story",
      videoId: "97909b3e6e53fa4d057891ae5fe71ea1", // ✅ Page 2 - Cloudflare Stream video
      text: "At the foot of the ancient Sharing Tree, the Squirrel Twins argued over the very last golden acorn.\n\nTessa wanted the acorn shell to make a tiny cup for gathering morning dew.\nToby wanted the nut inside because he'd missed breakfast and was starving."
    },
    {
      spread: 3,
      section: "story",
      videoId: "a9b9fccbde0418fb9624357f2035f6c7", // ✅ Page 3 - Cloudflare Stream video
      text: "They pulled the acorn back and forth until Mila stepped between them. Remembering what she'd learned about listening to feelings and needs, she asked gently, \"Can we each share why we want the acorn?\""
    },
    {
      spread: 4,
      section: "story",
      videoId: "573aec6bd199b19dfcdcf2feb5710297", // ✅ Page 4 - Cloudflare Stream video
      text: "Toby groaned, \"I'm so hungry, I might turn into a squirrel balloon and float away!\"\nTessa huffed, \"If I don't get the shell, I'll have to drink dew from my paws and they're ticklish!\"\nMax whispered to Mila, \"If they keep arguing, maybe the acorn will split itself just to get some peace.\""
    },
    {
      spread: 5,
      section: "story",
      videoId: "d86976e5b59e7944492b6b08c35381d4", // ✅ Page 5 - Cloudflare Stream video
      text: "After both twins explained, Mila's face lit up. \"You don't both need the whole acorn—you need different parts!\" Carefully Mila, cracked the acorn In two."
    },
    {
      spread: 6,
      section: "story",
      videoId: "80541971fde7ac56b990ba2ca000e347", // ✅ Page 6 - Cloudflare Stream video
      text: "Tessa polished the sturdy shell into a perfect little cup, and Toby happily munched the nut. The solution met both needs—and the quarrel dissolved into giggles."
    },
    {
      spread: 7,
      section: "cue",
      imageUrl: "https://static.step1.dev/600c259e67031c8d01f2e0798d3948c2", // ✅ Cue page - keeps static image (no video)
      cue: "Dew Cup",
      cueText: "Let's think of a clever way to share—can you come up with a win-win idea?"
    },
    {
      spread: 8,
      section: "story",
      videoId: "b8757958141f9a2fde1bb1ffdcdcaa91", // ✅ Page 8 - Cloudflare Stream video
      text: "The friends then used extra shells and nuts to help other animals: birds filled shell-cups with water for seedlings, and chipmunks shared nut pieces with a hungry fawn. Soon the base of the Sharing Tree buzzed with cooperation."
    },
    {
      spread: 9,
      section: "story",
      videoId: "335c1da020497ab52174dfb57b9689ec", // ✅ Page 9 - Cloudflare Stream video
      text: "Mila noticed that talking about needs—not just splitting things in half—made everyone feel closer and understood.\n\nAs sunset painted the forest orange and gold, the Twins gifted Mila a tiny dew cup. \"For the road,\" they said. Mila tucked it beside her other treasures, feeling the warm glow of true connection."
    }
  ]
};

interface Chapter6OptimizedProps {
  onBackToContents: () => void;
}

export default function Chapter6Optimized({ onBackToContents }: Chapter6OptimizedProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Initialize spread from URL parameter or default to 0
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());

  const handleCueComplete = () => {
    setCompletedCues(prev => new Set(prev).add('dew-cup-cue'));
  };

  // Interaction tracking ref (for button clicks, cue interactions)
  const interactionsRef = useRef<string[]>([]);

  const totalSpreads = chapter6Data.spreads.length;
  const currentSpreadData = chapter6Data.spreads[currentSpread];
  
  // NO AUTO-ADVANCE - User must click glowing icon to start reading
  
  // Reset spread when coming from bookmark
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);
  
  // Save bookmark when spread changes
  useEffect(() => {
    saveBookmark(6, currentSpread, chapter6Data.title);
  }, [currentSpread]);

  // Update platform tracking when position changes
  useEffect(() => {
    updateCurrentPosition(6, currentSpread);
  }, [currentSpread]);

  // Send analytics to PostgreSQL using new API
  useEffect(() => {
    const sendAnalyticsAsync = async () => {
      try {
        const { sendAnalytics } = await import('@/lib/analytics/client');
        await sendAnalytics(6, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 6, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
  }, [currentSpread]);

  const handlePrevious = useCallback(() => {
    // Track navigation interaction
    interactionsRef.current.push(`navigate_previous_spread_${currentSpread + 1}`);
    
    // Don't allow going back to map page (Spread 0)
    if (currentSpread > 1) {
      setCurrentSpread(prev => prev - 1);
    } else if (currentSpread === 1) {
      // If on Spread 1, go back to TOC instead of map
      navigate("/book/wtbtg");
    }
  }, [currentSpread, navigate]);

  const handleNext = useCallback(() => {
    // Track navigation interaction
    interactionsRef.current.push(`navigate_next_spread_${currentSpread + 1}`);
    
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(prev => prev + 1);
    } else {
      // Go to Activities page after the last story spread
      navigate("/book/chapter/6/activity");
    }
  }, [currentSpread, totalSpreads, navigate]);

  // Enhanced Activities button tracking with proper useCallback
  const handleActivities = useCallback(async () => {
    try {
      const { trackButton, sendAnalytics } = await import('@/lib/analytics/client');
      
      // Track the specific navigation interaction
      trackButton('activities_button', {
        navigation_source: 'chapter_nav',
        position: { x: 0, y: 0 } // Top right nav bar
      });
      
      // Send enhanced analytics immediately
      await sendAnalytics(6, currentSpread + 1);
      
      console.log(`📊 Enhanced Activities button tracking:`, {
        chapter: 6,
        spread: currentSpread + 1,
        buttonType: 'activities_button',
        navigationSource: 'chapter_nav'
      });
    } catch (error) {
      console.warn('Failed to track activities button:', error);
    }
    
    navigate(`/book/chapter/6/activity`);
  }, [navigate, currentSpread]);

  // Render video or image (VIDEO PLACEHOLDER READY)
  const renderVisual = (videoId?: string, imageUrl?: string) => {
    if (videoId) {
      // 🎬 VIDEO RENDERING (ready for when videos arrive)
      return (
        /* CLOUDFLARE STREAM VIDEO */
        <iframe
          key={videoId} // Force re-mount on video change
          src={`https://iframe.videodelivery.net/${videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
          style={{ 
            border: 'none', 
            width: '110%',  // 110% zoom (removes black bars - ADJUSTABLE)
            height: '110%', // 110% zoom
            position: 'relative',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none'
          }}
          allow="autoplay; encrypted-media"
          title={`Chapter Video`}
        />
      );
    } else if (imageUrl) {
      // 📷 FALLBACK IMAGE (until videos arrive)
      return (
        <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br from-cyan-100 to-teal-100 max-w-5xl mx-auto flex items-center justify-center">
          <motion.img
            src={imageUrl}
            alt={`Chapter 6 Spread ${currentSpread + 1}`}
            className="w-full h-full object-contain"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      );
    }
    return null;
  };

  // Full-screen MAP page (Spread 0 - Forest Journey Map)
  if (currentSpreadData.section === "map") {
    return (
      <div className="fixed inset-0 z-50">
        <ForestJourneyMap
          currentChapter={6}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-cyan-50 to-teal-50">
      
      {/* COMPACT HEADER - TRANSLUCENT */}
      <div className="bg-white/30 backdrop-blur-sm border-b border-cyan-200/50 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Back + Chapter Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Dira Stories</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button
              onClick={onBackToContents}
              variant="ghost"
              size="sm"
              className="text-forest-700 hover:bg-white/20 px-2 py-1 h-8"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <h1 className="text-lg font-bold text-forest-700">
              Chapter 6: The Sharing Tree <span className="text-sm font-normal text-forest-600">• Sharing & Connection</span>
            </h1>
          </div>
          
          {/* Right: Badge + Activities */}
          <div className="flex items-center gap-2">
            <Badge 
              className="text-xs px-2 py-1 border bg-white/50 backdrop-blur-sm" 
              style={{ 
                color: '#0891B2', 
                borderColor: '#A5F3FC' 
              }}
            >
              ☕ Dew Cup
            </Badge>
            
            <Button
              onClick={handleActivities}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1 h-8 border-cyan-300 text-cyan-700 bg-white/20 hover:bg-white/30"
            >
              🎯 Activities
            </Button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {currentSpreadData?.section === "cue" ? (
          /* CUE PAGE - FULL PAGE LAYOUT */
          <div 
            className="flex-1 relative"
            style={{
              backgroundImage: `url(${currentSpreadData.imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black/20"></div>
            
            {/* Centered cue box */}
            <div className="relative z-10 h-full flex items-center justify-center p-8">
              <motion.div 
                className="bg-white/95 backdrop-blur-sm border-4 border-cyan-400 rounded-3xl p-12 text-center max-w-2xl mx-auto shadow-xl"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                {/* Cue icon and title */}
                <div className="text-6xl mb-6">☕</div>
                <h2 className="text-3xl font-bold text-cyan-700 mb-8">
                  ✨ Magic Cue Found! ✨
                </h2>
                
                {/* Instruction text */}
                <p className="text-xl text-cyan-600 mb-8 leading-relaxed">
                  Click the glowing Dew Cup to practice sharing!
                </p>
              </motion.div>
            </div>

            {/* Cue Interaction positioned in corner */}
            <div className="absolute top-8 right-8 z-20">
              <Chapter6CueChoice
                onComplete={handleCueComplete}
                isCompleted={completedCues.has('dew-cup-cue')}
              />
            </div>
          </div>
        ) : (
          /* STORY PAGES - NEW UNIFIED 70/30 SPLIT LAYOUT (matching Chapter 4) */
          <>
            {/* TOP: VIDEO SECTION - 70% of screen height */}
            <div className="h-[70%] bg-transparent overflow-hidden relative pt-6">
              <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br max-w-5xl mx-auto">
                {currentSpreadData?.videoId ? (
                  /* CLOUDFLARE STREAM VIDEO */
                  <iframe
                    key={currentSpreadData.videoId} // Force re-mount on video change
                    src={`https://iframe.videodelivery.net/${currentSpreadData.videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
                    style={{ 
                      border: 'none', 
                      width: '130%',  // 130% zoom (removes black bars for Chapter 6)
                      height: '130%', // 130% zoom
                      position: 'relative',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                    allow="autoplay; encrypted-media"
                    title={`Chapter 6 Video`}
                  />
                ) : currentSpreadData?.imageUrl ? (
                  <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br from-cyan-100 to-teal-100 max-w-5xl mx-auto flex items-center justify-center">
                    <motion.img
                      src={currentSpreadData.imageUrl}
                      alt={`Chapter 6 Spread ${currentSpread + 1}`}
                      className="w-full h-full object-contain"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            
            {/* BOTTOM: TEXT SECTION - 30% of screen height */}
            <div className="h-[30%] flex flex-col px-8 py-6">
              <div className="flex-1 flex items-center">
                <div className="text-xl leading-relaxed text-gray-900 max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
                  {currentSpreadData?.text?.split('\n\n').map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mt-6' : ''}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* BOTTOM NAVIGATION - No hover effects (Chapter 4 style) */}
      <div className="px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between w-full">
            
            {/* Far Left: Prev Button */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handlePrevious}
              disabled={currentSpread === 0}
              className="text-forest-700 px-3 py-2 h-9"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Prev
            </Button>
            
            {/* Center: Progress Bar */}
            <div className="flex-1 mx-8 flex items-center gap-3">
              <span className="text-xs text-forest-600 whitespace-nowrap">Chapter Progress</span>
              <div className="flex-1 bg-transparent border border-white/30 rounded-full h-2">
                <div 
                  className="bg-cyan-500 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${((currentSpread + 1) / totalSpreads) * 100}%` }}
                ></div>
              </div>
              <span className="text-xs text-forest-500 whitespace-nowrap">
                {currentSpread} of {totalSpreads - 1}
              </span>
            </div>
            
            {/* Far Right: Next Button */}
            <Button 
              onClick={handleNext}
              variant="ghost"
              size="sm"
              className="text-forest-700 px-3 py-2 h-9"
            >
              {currentSpread === totalSpreads - 1 ? "Activities" : "Next"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
